import type { GraphCalendarEvent, CalendarEvent, EventStatus } from '@/types/calendarTypes';
import type { EventSummary } from '@/store/models/events/entities';
import type { EventState } from '@/store/models/events/event-state';
import { buildSeriesOrdinalTitleMap } from './seriesEventTitle';

/** Map upper-case EventState (API) -> lower-case EventStatus (calendar UI) */
export function eventStateToStatus(state: EventState): EventStatus {
  switch (state) {
    case 'DRAFT':
      return 'draft';
    case 'SUBMITTED':
      return 'submitted';
    case 'UNDER_REVIEW':
      return 'under_review';
    case 'CONFIRMED':
      return 'confirmed';
    case 'DECLINED':
      return 'declined';
    case 'SUBMITTED_FOR_CANCELLATION':
      return 'request_for_cancellation';
    case 'CANCELLED':
      return 'cancelled';
    case 'COMPLETED':
      return 'completed';
    case 'NO_OCCURRENCE':
      return 'did_not_occur';
    default:
      return 'submitted';
  }
}

/**
 * Map API EventSummary[] (GET /api/v1/events) -> calendar grid items.
 * Only the fields needed by Day/Week/Month grids are populated; rich
 * resource fields are loaded later by the detail drawer via useEventDetails.
 */
export function mapEventSummariesToCalendarEvents(summaries: EventSummary[]): CalendarEvent[] {
  const displayTitleByEventId = buildSeriesOrdinalTitleMap(
    summaries.map((summary) => ({
      eventId: String(summary.eventId),
      seriesIndex: summary.seriesIndex,
      seriesTotal: summary.seriesTotal,
      eventTitle: summary.eventTitle ?? '(Untitled event)',
    })),
    (event) => event.eventTitle
  );

  return summaries.map((s) => {
    const start = new Date(s.eventStartDate);
    const end = new Date(s.eventEndDate);
    const spaces = s.spaces ?? [];
    const spaceName = spaces.length > 0 ? spaces[0]!.spaceName : (s.spaceName ?? '');
    const additionalRooms = spaces.length > 1 ? spaces.slice(1).map((sp) => sp.spaceName) : [];
    const campus = s.buildingName ?? '';
    const room = spaceName || campus;
    return {
      id: s.eventId,
      title: displayTitleByEventId.get(String(s.eventId))!,
      start,
      end,
      allDay: false,
      resource: {
        refId: s.requestId ?? s.eventId,
        status: eventStateToStatus(s.eventState),
        location: room,
        campus,
        room,
        additionalRooms,
        attendeeCount: s.numberOfAttendees ?? 0,
        capacity: 0,
        organizer: s.createdBy ?? '',
        requester: s.submittedBy ?? s.createdBy ?? '',
        planner: '',
        bodyPreview: '',
        catering: '',
        equipment: [],
        buildingId: s.buildingId,
        ownerEmail: s.submittedByEmail ?? '',
        plannerAssignedEmail: s.plannerAssignedEmail,
        plannerAssignedName: s.plannerAssignedName,
      },
    };
  });
}

/** Map Graph API events to our internal CalendarEvent format */
export function mapGraphEvents(graphEvents: GraphCalendarEvent[]): CalendarEvent[] {
  return graphEvents.map((e) => ({
    id: e.id,
    title: e.subject,
    start: new Date(e.start.dateTime),
    end: new Date(e.end.dateTime),
    allDay: e.isAllDay,
    resource: {
      refId: `REF-${e.id}`,
      status: e.isCancelled ? 'cancelled' : e.responseStatus.response,
      location: e.location.displayName,
      campus: 'Redmond Campus',
      room: e.location.displayName,
      additionalRooms: [],
      attendeeCount: e.attendees.length,
      capacity: e.attendees.length + Math.floor(Math.random() * 10) + 2,
      organizer: e.organizer.emailAddress.name,
      requester: e.organizer.emailAddress.name,
      planner: 'Sarah Chen',
      bodyPreview: e.bodyPreview,
      catering: e.attendees.length > 3 ? `Lunch for ${e.attendees.length}` : '',
      equipment:
        e.location.locationType === 'conferenceRoom' ? ['Video conferencing', 'Whiteboard'] : [],
    },
  }));
}
